import { Field, ErrorMessage, useFormikContext } from 'formik';
import {
  FieldEntityProps,
  FieldClassNameInterface,
  SelectOptions,
  ObjStrKeyStrVal,
} from '../types';
import { getSelectOptions } from '../utils';

interface AccessibleDependentSelectFieldProps {
  field: FieldEntityProps;
  placeholder: string;
  dependentSelectOptions?: SelectOptions[];
  selectFieldClassNames: FieldClassNameInterface;
  isTouchedAndError: boolean;
  isRequired: boolean;
  showErrorMessages?: boolean;
}

const AccessibleDependentSelectField = ({
  field,
  placeholder,
  dependentSelectOptions,
  selectFieldClassNames,
  isTouchedAndError,
  isRequired,
  showErrorMessages = false,
}: AccessibleDependentSelectFieldProps) => {
  const { values, touched, setFieldValue } =
    useFormikContext<ObjStrKeyStrVal>();
  const programTypeTouchedAndNotEmpty =
    !!touched.program_type && values.program_type !== '';

  let options: string[] = [];
  if (programTypeTouchedAndNotEmpty && dependentSelectOptions) {
    options = getSelectOptions(values.program_type, dependentSelectOptions);
  }

  const errorClass = isTouchedAndError ? 'errorBorder' : '';
  const errorId = `${field.parameterName}-error`;

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFieldValue(field.parameterName, e.target.value);
  };

  // Only show the populated select if program_type has been selected
  if (programTypeTouchedAndNotEmpty && options && options.length > 0) {
    return (
      <>
        <Field
          as="select"
          name={field.parameterName}
          id={field.parameterName}
          className={`${selectFieldClassNames.mainField} ${errorClass}`}
          onChange={handleChange}
          aria-label={field.name}
          aria-required={isRequired}
          aria-invalid={isTouchedAndError}
          aria-describedby={
            showErrorMessages && isTouchedAndError ? errorId : undefined
          }
        >
          <option value="">{placeholder}</option>
          {options.map((opt, key) => (
            <option key={key} value={opt}>
              {opt}
            </option>
          ))}
        </Field>
        {showErrorMessages && (
          <ErrorMessage name={field.parameterName}>
            {(msg) => (
              <div
                id={errorId}
                role="alert"
                className={isTouchedAndError ? 'errorMessage' : 'errorHide'}
              >
                {msg}
              </div>
            )}
          </ErrorMessage>
        )}
      </>
    );
  }

  // Default state: show disabled/placeholder select
  return (
    <>
      <Field
        as="select"
        name={field.parameterName}
        id={field.parameterName}
        className={selectFieldClassNames.mainField}
        aria-label={field.name}
        aria-required={isRequired}
      >
        <option value="">{placeholder}</option>
      </Field>
      {showErrorMessages && (
        <ErrorMessage name={field.parameterName}>
          {(msg) => (
            <div
              id={errorId}
              role="alert"
              className={isTouchedAndError ? 'errorMessage' : 'errorHide'}
            >
              {msg}
            </div>
          )}
        </ErrorMessage>
      )}
    </>
  );
};

export default AccessibleDependentSelectField;
